import { useEffect } from 'react';

/**
 * useScreenshotProtection
 *
 * Applied globally via Layout so it covers every authenticated page.
 *
 * What it does:
 *  • PrintScreen key  — shows a full-screen warning overlay for 2.5 s.
 *  • Ctrl+P (print)   — prevented silently.
 *  • Ctrl+S (save)    — prevented silently.
 *  • F12 (DevTools)   — prevented silently.
 */
export function useScreenshotProtection() {
  useEffect(() => {
    let hideTimer = null;

    const OVERLAY_ID = '__aps-screenshot-guard';

    const showOverlay = () => {
      document.getElementById(OVERLAY_ID)?.remove();
      if (hideTimer) clearTimeout(hideTimer);

      const overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.style.cssText = [
        'position:fixed',
        'inset:0',
        'z-index:99999',
        'background:#0f1f45',
        'display:flex',
        'flex-direction:column',
        'align-items:center',
        'justify-content:center',
        'color:#fff',
        'font-family:system-ui,sans-serif',
        'user-select:none',
        'pointer-events:none',
      ].join(';');

      overlay.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24"
          fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          style="margin-bottom:16px">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <h2 style="margin:0 0 8px;font-size:22px;font-weight:700;letter-spacing:.5px">
          Screenshot Restricted
        </h2>
        <p style="margin:0 0 6px;font-size:15px;opacity:.85;text-align:center;max-width:340px">
          Screenshots of this application are not permitted.
        </p>
        <p style="margin:0;font-size:12px;opacity:.55">
          This action has been logged.
        </p>
      `;

      document.body.appendChild(overlay);

      hideTimer = setTimeout(() => {
        overlay.remove();
        hideTimer = null;
      }, 2500);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        showOverlay();
        return;
      }
      // Ctrl+P — print dialog
      if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        return;
      }
      // Ctrl+S — save page
      if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        return;
      }
      // F12 — DevTools shortcut
      if (e.key === 'F12') {
        e.preventDefault();
        return;
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'PrintScreen') {
        try {
          navigator.clipboard.writeText('').catch(() => {});
        } catch (_) { /* clipboard API may be restricted */ }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      if (hideTimer) clearTimeout(hideTimer);
      document.getElementById(OVERLAY_ID)?.remove();
    };
  }, []);
}
